import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import shallow from "zustand/shallow";

import eventService from "@/js/services/eventService";
import useStore from "@/stores/index";
import { getChatWindowOpen, getMessagesForThreadKey } from "@/stores/chatStore";
import { isMembershipOwnershipDisabled } from "@/stores/classwizeConfigStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { sendChatNotificaton } from "@/js/utils/notifications";
import useClickOutside from "@/js/utils/outsideClickUtils";
import { userDisplayText } from "@/js/utils/userUtils";
import { isChatFeatureEnabled } from "@/js/utils/chatUtils";
import { ClassroomOverviewDTO } from "@/stores/classroomStore_I";
import { ChatEventData } from "./types";
import ChatToolbarButton from "./components/ChatToolbarButton";
import ChatWindow from "./components/ChatWindow";
import DisplayNameModal from "./components/DisplayNameModal";

interface Props {
	/** Classroom object containing class information */
	classroom?: ClassroomOverviewDTO;
	/** Reference to navigation element for offset calculations */
	navRef?: React.RefObject<HTMLElement>;
}

const ClassroomChat: React.FC<Props> = ({ classroom, ...props }) => {
	const location = useLocation();

	const [isOpen, setIsOpen] = useState<boolean>(getChatWindowOpen(useStore.getState()));
	const [isEnabled, setIsEnabled] = useState<boolean>(false);
	const [, setAllowPushNotifications] = useState<boolean>(false);
	const [windowOffset, setWindowOffset] = useState<number>(0);
	const chatWrapperRef = useRef<HTMLDivElement>(null);

	const scrollHandler = (): void => {
		if (props.navRef && props.navRef.current) {
			const rect = props.navRef.current.getBoundingClientRect();

			if (rect.top && rect.top <= 0) {
				setWindowOffset(rect.top);
			} else {
				setWindowOffset(0);
			}
		}
	};

	// @ts-expect-error - Leaving code as it is
	useEffect(() => {
		if (isEnabled) {
			window.addEventListener("scroll", scrollHandler, { capture: true });

			useStore.getState().chat.fetchOverview();
			addEventServiceListeners();
			const unsubEventServiceStatus = useStore.subscribe(
				// @ts-expect-error - Leaving code as it is
				(state) => state.eventServiceStatus.status,
				() => {
					addEventServiceListeners();
				}
			);
			// @ts-expect-error - Leaving code as it is
			useStore.getState().chat.fetchDisplayName({ username: localStorage.getItem("USERNAME") });
			const unsubChatOpen = useStore.subscribe(
				// @ts-expect-error - Leaving code as it is
				(state) => state.chat.chatWindowOpen,
				// @ts-expect-error - Leaving code as it is
				(newisOpen) => setIsOpen(newisOpen),
				shallow
			);

			if ("Notification" in window) {
				if (Notification.permission === "granted") {
					setAllowPushNotifications(true);
				} else if (Notification.permission !== "denied" && Notification.permission === "default") {
					Notification.requestPermission((permission) => {
						setAllowPushNotifications(permission === "granted");
					});
				}
			}

			return () => {
				window.removeEventListener("scroll", scrollHandler, { capture: true });
				unsubChatOpen();
				unsubEventServiceStatus();
				removeEventServiceListeners();
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEnabled]);

	useEffect(() => {
		if (useStore.getState().chat.displayName.error && !useStore.getState().chat.displayName.isFetching) {
			// @ts-expect-error - Leaving code as it is
			useStore.getState().chat.fetchDisplayName({ username: localStorage.getItem("USERNAME") });
		}
		if (useStore.getState().chat.unreadMessagesCount.error && !useStore.getState().chat.unreadMessagesCount.isFetching) {
			useStore.getState().chat.fetchOverview();
		}
	}, [isOpen]);

	useEffect(() => {
		if (isChatFeatureEnabled()) {
			// chat is not available if membership and ownership are both disabled
			if (isMembershipOwnershipDisabled()) {
				return;
			}

			setIsEnabled(true);
		}
	}, []);

	const isNotificationForCurrentClassroomInView = (messageClassroomId: string): boolean => {
		return location.pathname === "/classroom/details" && messageClassroomId === localStorage.getItem("ACTIVE_GROUP");
	};

	const addEventServiceListeners = (): void => {
		if (eventService.eventSource) {
			// @ts-expect-error - Leaving code as it is
			eventService.eventSource.addEventListener("MESSAGE", handleMessage);
		}
	};

	const removeEventServiceListeners = (): void => {
		if (eventService.eventSource) {
			// @ts-expect-error - Leaving code as it is
			eventService.eventSource.removeEventListener("MESSAGE", handleMessage);
		}
	};

	const handleMessage = useCallback((event: MessageEvent): void => {
		const chat = useStore.getState().chat;

		const currentUser = localStorage.getItem("USERNAME");
		let eventData: ChatEventData;
		try {
			eventData = JSON.parse(event.data) as ChatEventData;
		} catch (err) {
			console.log(err);
			return;
		}
		if (!eventData.participantIds.includes(currentUser || "")) {
			console.log("Recieved message for other user");
			return;
		}

		// @ts-expect-error - Leaving code as it is
		useStore.getState().chat.fetchMessages(
			{
				classroomId: eventData.classroomId,
				threadKey: eventData.threadKey,
				maxResults: 5,
			},
			true,
			false,
			true
		);

		getMessagesForThreadKey(useStore.getState(), eventData.threadKey);

		if (eventData.sender.id !== currentUser) {
			const currentClass = useUserSessionStore.getState().getActiveGroup();
			let showClassroomName = false;

			if (currentClass !== eventData.classroomId) {
				showClassroomName = true;
			}

			// @ts-expect-error - Leaving code as it is
			const users = useStore.getState().users.data;
			const senderUser = users[eventData.sender.id];
			const senderDisplayText = senderUser ? userDisplayText(senderUser) : "";

			// we also only want to do push notifications for messages sent from students in this class
			if (isNotificationForCurrentClassroomInView(eventData.classroomId) && Object.keys(users).includes(eventData.sender.id)) {
				sendChatNotificaton(
					eventData.threadKey,
					eventData.classroomId,
					eventData.sender.id,
					eventData.msg,
					showClassroomName,
					senderDisplayText
				);
			}

			const chatOverview = chat.unreadMessagesCount.data;

			if (chat.selectedThreadKey !== eventData.threadKey) {
				if (chatOverview[eventData.threadKey] && !!chatOverview[eventData.threadKey].hasUnreadMessages) {
					chat.updateChatOverviewData(chatOverview);
				} else {
					chat.fetchOverview();
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const toggleIsOpen = (): void => {
		// @ts-expect-error - Leaving code as it is
		useStore.getState().chat.toggleChatWindow();
	};

	const handleClickOutside = (): void => {
		// @ts-expect-error - Leaving code as it is
		if (isOpen && !useStore.getState().chat.chatToggleModalOpen) {
			// @ts-expect-error - Leaving code as it is
			useStore.getState().chat.toggleChatWindow();
		}
	};

	useClickOutside([chatWrapperRef], handleClickOutside);

	if (!isEnabled) {
		return null;
	}

	return (
		<div className="ClassroomChat_wrapper" ref={chatWrapperRef}>
			{/* @ts-expect-error - Leaving code as it is */}
			<ChatToolbarButton handleClick={toggleIsOpen} classroom={classroom} {...props} />
			{isOpen && (
				<ChatWindow classroom={classroom} isChatWorking={!!useStore.getState().chat.unreadMessagesCount.data} windowOffset={windowOffset} />
			)}
			<DisplayNameModal />
		</div>
	);
};

export default ClassroomChat;
